<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

const NXS_KEY = 'nxs_mail_r4k7p2w9x1m3';
const API = '/mail-api/nxs';
const SEND_API = '/mail-api/nxs/send';

const route = useRoute();
const { accountScopedRoute } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const currentUserId = useMapGetter('getCurrentUserID');

const state = ref('loading');
const mailUser = ref(null);
const accounts = ref([]);
const emails = ref([]);
const selectedEmail = ref(null);
const threadEmails = ref([]);
const searchQuery = ref('');
const composeOpen = ref(false);
const composeFrom = ref('');
const composeTo = ref('');
const composeCc = ref('');
const composeBcc = ref('');
const showCcBcc = ref(false);
const composeSubject = ref('');
const composeBody = ref('');
const composeFiles = ref([]);
const composeSending = ref(false);
const composeError = ref('');
const settingsOpen = ref(false);
const signatureText = ref('');
const editingAccountId = ref(null);
const domains = ref([]);
const newAccountLocal = ref('');
const newAccountDomain = ref('');
const newAccountName = ref('');
const createAccountOpen = ref(false);

const folder = computed(() => route.params.folder || 'inbox');

const folderConfig = {
  inbox: { label: 'Caixa de Entrada', icon: 'i-lucide-inbox', params: { type: 'inbound', spam: false } },
  sent: { label: 'Enviados', icon: 'i-lucide-send', params: { type: 'outbound' } },
  starred: { label: 'Favoritos', icon: 'i-lucide-star', params: { starred: true } },
  spam: { label: 'SPAM', icon: 'i-lucide-shield-alert', params: { spam: true } },
  trash: { label: 'Lixeira', icon: 'i-lucide-trash-2', params: {} },
};

const currentFolder = computed(() => folderConfig[folder.value] || folderConfig.inbox);
const unreadCount = computed(() => emails.value.filter(e => !e.read).length);

function headers() {
  return {
    'Content-Type': 'application/json',
    'X-NXS-Key': NXS_KEY,
    'X-NXS-Chatwoot-ID': String(currentUserId.value || ''),
    'X-NXS-Chatwoot-Name': currentUser.value?.name || currentUser.value?.available_name || 'Agente',
  };
}

async function nxs(action, extra = {}) {
  const res = await fetch(API, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ action, ...extra }),
  });
  return res.json();
}

async function auth() {
  if (!currentUserId.value) { state.value = 'error'; return; }
  try {
    const data = await nxs('auth');
    if (data.error) { state.value = 'error'; return; }
    mailUser.value = data.user;
    accounts.value = data.accounts || [];
    if (accounts.value.length > 0) composeFrom.value = accounts.value[0].address;
    state.value = 'ready';
    loadEmails();
  } catch {
    state.value = 'error';
  }
}

async function loadEmails() {
  if (folder.value === 'trash') { emails.value = []; return; }
  const params = { ...currentFolder.value.params };
  if (searchQuery.value) params.q = searchQuery.value;
  const data = await nxs('list_emails', params);
  if (Array.isArray(data)) emails.value = data;
}

async function openEmail(email) {
  selectedEmail.value = email;
  threadEmails.value = [];

  if (!email.read) {
    await nxs('update_email', { id: email.id, read: true });
    email.read = true;
  }

  if (email.threadId && (email.threadCount || 0) > 1) {
    const thread = await nxs('list_emails', {
      type: 'inbound',
      spam: false,
      threadId: email.threadId,
    });
    if (Array.isArray(thread)) {
      threadEmails.value = thread;
      for (const te of thread) {
        if (!te.read) {
          nxs('update_email', { id: te.id, read: true });
          te.read = true;
        }
      }
    } else {
      threadEmails.value = [email];
    }
  } else {
    if (!email.html && !email._loaded) {
      const full = await nxs('get_email', { id: email.id });
      if (full && !full.error) {
        Object.assign(email, full);
        email._loaded = true;
      }
    }
    threadEmails.value = [email];
  }
}

function closeEmail() {
  selectedEmail.value = null;
  threadEmails.value = [];
}

async function toggleStar(email, ev) {
  if (ev) ev.stopPropagation();
  email.starred = !email.starred;
  await nxs('update_email', { id: email.id, starred: email.starred });
}

async function markSpam(email) {
  await nxs('update_email', { id: email.id, spam: true, blockDomain: true });
  selectedEmail.value = null;
  threadEmails.value = [];
  loadEmails();
}

async function unmarkSpam(email) {
  await nxs('update_email', { id: email.id, spam: false });
  selectedEmail.value = null;
  threadEmails.value = [];
  loadEmails();
}

async function deleteEmail(email) {
  await nxs('delete_email', { id: email.id });
  selectedEmail.value = null;
  threadEmails.value = [];
  emails.value = emails.value.filter(e => e.id !== email.id);
}

function openCompose(replyTo) {
  composeOpen.value = true;
  composeError.value = '';
  if (replyTo) {
    composeTo.value = replyTo.type === 'inbound' ? replyTo.from : replyTo.to;
    composeSubject.value = replyTo.subject.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject}`;
    composeBody.value = `\n\n--- ${fmtDate(replyTo.createdAt)} ${replyTo.from} escreveu ---\n${replyTo.body || ''}`;
  } else {
    composeTo.value = '';
    composeSubject.value = '';
    composeBody.value = '';
  }
  composeCc.value = '';
  composeBcc.value = '';
  showCcBcc.value = false;
  filePreviews.value.forEach(e => { if (e.url) URL.revokeObjectURL(e.url); });
  filePreviews.value = [];
  composeFiles.value = [];
}

function replyToThread() {
  const lastEmail = threadEmails.value.length > 0
    ? threadEmails.value[threadEmails.value.length - 1]
    : selectedEmail.value;
  openCompose(lastEmail);
}

function closeCompose() {
  filePreviews.value.forEach(e => { if (e.url) URL.revokeObjectURL(e.url); });
  filePreviews.value = [];
  composeOpen.value = false;
}

const filePreviews = ref([]);

function onFileSelect(ev) {
  const files = Array.from(ev.target.files || []);
  files.forEach(f => {
    const entry = { file: f, url: null };
    if (f.type.startsWith('image/')) {
      entry.url = URL.createObjectURL(f);
    }
    filePreviews.value.push(entry);
  });
  composeFiles.value = filePreviews.value.map(e => e.file);
}

function removeFile(idx) {
  const entry = filePreviews.value[idx];
  if (entry?.url) URL.revokeObjectURL(entry.url);
  filePreviews.value.splice(idx, 1);
  composeFiles.value = filePreviews.value.map(e => e.file);
}

function fileIcon(file) {
  const t = file.type || '';
  if (t.startsWith('image/')) return 'i-lucide-image';
  if (t === 'application/pdf') return 'i-lucide-file-text';
  if (t.includes('spreadsheet') || t.includes('excel') || file.name.match(/\.(xlsx?|csv)$/i)) return 'i-lucide-file-spreadsheet';
  if (t.includes('word') || t.includes('document') || file.name.match(/\.docx?$/i)) return 'i-lucide-file-type';
  if (t.startsWith('video/')) return 'i-lucide-film';
  if (t.startsWith('audio/')) return 'i-lucide-music';
  if (t.includes('zip') || t.includes('rar') || t.includes('compressed')) return 'i-lucide-archive';
  return 'i-lucide-file';
}

function fileSizeLabel(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

async function sendMail() {
  if (!composeFrom.value || !composeTo.value || !composeSubject.value) {
    composeError.value = 'Preencha remetente, destinatário e assunto';
    return;
  }
  composeSending.value = true;
  composeError.value = '';
  try {
    const form = new FormData();
    form.append('from', composeFrom.value);
    form.append('to', composeTo.value);
    if (composeCc.value.trim()) form.append('cc', composeCc.value.trim());
    if (composeBcc.value.trim()) form.append('bcc', composeBcc.value.trim());
    form.append('subject', composeSubject.value);
    form.append('body', composeBody.value);
    composeFiles.value.forEach(f => form.append('attachments', f));
    const h = {
      'X-NXS-Key': NXS_KEY,
      'X-NXS-Chatwoot-ID': String(currentUserId.value || ''),
      'X-NXS-Chatwoot-Name': currentUser.value?.name || 'Agente',
    };
    const res = await fetch(SEND_API, { method: 'POST', headers: h, body: form });
    const data = await res.json();
    if (data.error) { composeError.value = data.error; }
    else { composeOpen.value = false; loadEmails(); }
  } catch { composeError.value = 'Erro de rede'; }
  composeSending.value = false;
}

async function openSettings() {
  settingsOpen.value = true;
  const d = await nxs('list_domains');
  if (Array.isArray(d)) domains.value = d;
}

function editSignature(account) {
  editingAccountId.value = account.id;
  signatureText.value = account.signature || '';
}

async function saveSignature() {
  await nxs('update_account', { id: editingAccountId.value, signature: signatureText.value });
  const acc = accounts.value.find(a => a.id === editingAccountId.value);
  if (acc) acc.signature = signatureText.value;
  editingAccountId.value = null;
}

async function createAccount() {
  if (!newAccountLocal.value || !newAccountDomain.value) return;
  const data = await nxs('create_account', {
    localPart: newAccountLocal.value,
    domainId: newAccountDomain.value,
    name: newAccountName.value,
  });
  if (data.error) { composeError.value = data.error; return; }
  accounts.value.push(data);
  if (!composeFrom.value) composeFrom.value = data.address;
  createAccountOpen.value = false;
  newAccountLocal.value = '';
  newAccountDomain.value = '';
  newAccountName.value = '';
}

function fmtDate(d) {
  const dt = new Date(d);
  const now = new Date();
  const isToday = dt.toDateString() === now.toDateString();
  if (isToday) return dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function fmtDateFull(d) {
  return new Date(d).toLocaleString('pt-BR', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDateShort(d) {
  return new Date(d).toLocaleString('pt-BR', {
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}

function extractName(addr) {
  if (!addr) return '';
  const m = addr.match(/^"?([^"<]+)"?\s*</);
  if (m) return m[1].trim();
  return addr.split('@')[0];
}

function extractEmail(addr) {
  if (!addr) return addr;
  const m = addr.match(/<(.+)>/);
  return m ? m[1] : addr;
}

function preview(body) {
  if (!body) return '';
  return body.replace(/\n/g, ' ').slice(0, 120);
}

function parseAttachments(str) {
  try { return JSON.parse(str || '[]'); } catch { return []; }
}

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

watch(folder, () => { selectedEmail.value = null; threadEmails.value = []; loadEmails(); });
watch(searchQuery, () => { loadEmails(); });

onMounted(() => { auth(); });
</script>

<template>
  <div class="em-root">
    <!-- LOADING -->
    <div v-if="state === 'loading'" class="em-center">
      <div class="em-spinner" />
      <span class="em-muted">Conectando ao servidor de email...</span>
    </div>

    <!-- ERROR -->
    <div v-if="state === 'error'" class="em-center">
      <span class="i-lucide-mail-x em-error-icon" />
      <span class="em-muted">Não foi possível conectar</span>
      <button class="em-btn em-btn-primary" @click="state = 'loading'; auth();">Tentar novamente</button>
    </div>

    <!-- READY -->
    <template v-if="state === 'ready'">
      <!-- TOOLBAR -->
      <div class="em-toolbar">
        <div class="em-toolbar-left">
          <span :class="currentFolder.icon" class="em-toolbar-icon" />
          <h2 class="em-toolbar-title">{{ currentFolder.label }}</h2>
          <span v-if="unreadCount && folder === 'inbox'" class="em-badge">{{ unreadCount }}</span>
        </div>
        <div class="em-toolbar-right">
          <div class="em-search-box">
            <span class="i-lucide-search em-search-icon" />
            <input
              v-model="searchQuery"
              placeholder="Pesquisar emails..."
              class="em-search-input"
            />
          </div>
          <button class="em-btn em-btn-primary em-btn-compose" @click="openCompose(null)">
            <span class="i-lucide-pen-line" /> Escrever
          </button>
          <button class="em-btn em-btn-ghost" @click="openSettings" title="Contas de email">
            <span class="i-lucide-settings" />
          </button>
        </div>
      </div>

      <!-- CONTENT -->
      <div class="em-content">
        <!-- EMAIL LIST -->
        <div v-if="!selectedEmail" class="em-list-container">
          <!-- Empty -->
          <div v-if="emails.length === 0" class="em-center em-empty">
            <span :class="currentFolder.icon" class="em-empty-icon" />
            <span class="em-muted">
              <template v-if="folder === 'trash'">A lixeira está vazia</template>
              <template v-else>Nenhum email encontrado</template>
            </span>
          </div>

          <!-- List -->
          <div v-else class="em-list">
            <div
              v-for="email in emails"
              :key="email.id"
              class="em-row"
              :class="{ 'em-row--unread': !email.read }"
              @click="openEmail(email)"
            >
              <button
                class="em-star-btn"
                :class="{ 'em-star-btn--active': email.starred }"
                @click="toggleStar(email, $event)"
              >
                <span :class="email.starred ? 'i-lucide-star' : 'i-lucide-star'" />
              </button>
              <div class="em-row-sender">
                {{ email.type === 'outbound' ? `Para: ${extractName(email.to)}` : extractName(email.from) }}
                <span v-if="(email.threadCount || 0) > 1" class="em-thread-badge">
                  <span class="i-lucide-message-square em-thread-icon" />
                  {{ email.threadCount }}
                </span>
              </div>
              <div class="em-row-content">
                <span class="em-row-subject">{{ email.subject || '(sem assunto)' }}</span>
                <span class="em-row-preview"> — {{ preview(email.body) }}</span>
              </div>
              <div class="em-row-meta">
                <span v-if="parseAttachments(email.attachments).length" class="i-lucide-paperclip em-row-attach" />
                <span class="em-row-date">{{ fmtDate(email.createdAt) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- EMAIL DETAIL — Thread View -->
        <div v-else class="em-detail">
          <div class="em-detail-toolbar">
            <button class="em-btn em-btn-ghost" @click="closeEmail">
              <span class="i-lucide-arrow-left" /> Voltar
            </button>
            <div class="em-detail-actions">
              <button class="em-btn em-btn-ghost" @click="replyToThread" title="Responder">
                <span class="i-lucide-reply" />
              </button>
              <button class="em-btn em-btn-ghost" @click="toggleStar(selectedEmail)" :title="selectedEmail.starred ? 'Desfavoritar' : 'Favoritar'">
                <span class="i-lucide-star" :class="{ 'em-star-active': selectedEmail.starred }" />
              </button>
              <button v-if="folder === 'spam'" class="em-btn em-btn-ghost" @click="unmarkSpam(selectedEmail)" title="Não é spam">
                <span class="i-lucide-shield-check" />
              </button>
              <button v-else class="em-btn em-btn-ghost" @click="markSpam(selectedEmail)" title="Marcar como spam (bloqueia o domínio)">
                <span class="i-lucide-shield-alert" />
              </button>
              <button class="em-btn em-btn-ghost em-btn-danger" @click="deleteEmail(selectedEmail)" title="Excluir">
                <span class="i-lucide-trash-2" />
              </button>
            </div>
          </div>

          <div class="em-detail-header">
            <h2 class="em-detail-subject">{{ selectedEmail.subject || '(sem assunto)' }}</h2>
            <div v-if="threadEmails.length > 1" class="em-thread-info">
              <span class="i-lucide-message-square" />
              {{ threadEmails.length }} mensagens nesta conversa
            </div>
          </div>

          <div class="em-detail-body">
            <div
              v-for="(msg, idx) in threadEmails"
              :key="msg.id"
              class="em-thread-message"
              :class="{ 'em-thread-message--border': idx > 0 }"
            >
              <div class="em-detail-meta">
                <div class="em-detail-avatar" :style="{ background: msg.type === 'outbound' ? 'rgb(var(--green-9))' : 'rgb(var(--blue-9))' }">
                  {{ extractName(msg.from).charAt(0).toUpperCase() }}
                </div>
                <div class="em-detail-info">
                  <div class="em-detail-from">
                    {{ extractName(msg.from) }}
                    <span class="em-detail-email">&lt;{{ extractEmail(msg.from) }}&gt;</span>
                  </div>
                  <div class="em-detail-to">para {{ msg.to }}</div>
                </div>
                <div class="em-detail-date">{{ fmtDateShort(msg.createdAt) }}</div>
              </div>

              <div class="em-msg-body">
                <div v-if="msg.html" v-html="msg.html" class="em-html-body" />
                <pre v-else class="em-text-body">{{ msg.body }}</pre>
              </div>

              <div v-if="parseAttachments(msg.attachments).length" class="em-detail-attachments">
                <div class="em-attach-label">
                  <span class="i-lucide-paperclip" /> {{ parseAttachments(msg.attachments).length }} anexo(s)
                </div>
                <div class="em-attach-list">
                  <div v-for="(att, i) in parseAttachments(msg.attachments)" :key="i" class="em-attach-item">
                    <span class="i-lucide-file" />
                    <span>{{ att.filename }}</span>
                    <span class="em-muted">{{ fmtSize(att.size) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- COMPOSE MODAL -->
    <Teleport to="body">
      <div v-if="composeOpen" class="em-modal-backdrop" @click.self="closeCompose">
        <div class="em-compose">
          <div class="em-compose-header">
            <h3>Nova mensagem</h3>
            <button class="em-btn em-btn-ghost em-btn-sm" @click="closeCompose">
              <span class="i-lucide-x" />
            </button>
          </div>

          <div v-if="composeError" class="em-alert">{{ composeError }}</div>

          <div class="em-compose-scroll">
          <div class="em-compose-fields">
            <div class="em-compose-field">
              <label>De</label>
              <select v-model="composeFrom" class="em-select">
                <option v-for="acc in accounts" :key="acc.id" :value="acc.address">
                  {{ acc.name ? `${acc.name} <${acc.address}>` : acc.address }}
                </option>
              </select>
            </div>
            <div class="em-compose-field">
              <div class="em-field-row">
                <label>Para</label>
                <button v-if="!showCcBcc" class="em-ccbcc-toggle" @click="showCcBcc = true">Cc / Cco</button>
              </div>
              <input v-model="composeTo" type="email" placeholder="email@exemplo.com" class="em-input" />
            </div>
            <div v-if="showCcBcc" class="em-compose-field">
              <label>Cc</label>
              <input v-model="composeCc" type="email" placeholder="Cópia (separar com vírgula)" class="em-input" />
            </div>
            <div v-if="showCcBcc" class="em-compose-field">
              <label>Cco</label>
              <input v-model="composeBcc" type="email" placeholder="Cópia oculta (separar com vírgula)" class="em-input" />
            </div>
            <div class="em-compose-field">
              <label>Assunto</label>
              <input v-model="composeSubject" placeholder="Assunto do email" class="em-input" />
            </div>
          </div>

          <textarea
            v-model="composeBody"
            class="em-compose-body"
            placeholder="Escreva sua mensagem..."
          />

          <!-- Attachment previews -->
          <div v-if="filePreviews.length" class="em-attach-preview-area">
            <div class="em-attach-preview-label">
              <span class="i-lucide-paperclip" />
              {{ filePreviews.length }} anexo(s)
            </div>
            <div class="em-attach-grid">
              <div v-for="(entry, i) in filePreviews" :key="i" class="em-attach-card">
                <button class="em-attach-card-remove" @click="removeFile(i)" title="Remover">
                  <span class="i-lucide-x" />
                </button>
                <div v-if="entry.url" class="em-attach-card-img">
                  <img :src="entry.url" :alt="entry.file.name" />
                </div>
                <div v-else class="em-attach-card-icon">
                  <span :class="fileIcon(entry.file)" />
                </div>
                <div class="em-attach-card-info">
                  <span class="em-attach-card-name">{{ entry.file.name }}</span>
                  <span class="em-attach-card-size">{{ fileSizeLabel(entry.file.size) }}</span>
                </div>
              </div>
            </div>
          </div>

          </div><!-- /em-compose-scroll -->

          <div class="em-compose-footer">
            <div class="em-compose-footer-left">
              <label class="em-btn em-btn-ghost em-btn-sm em-attach-btn">
                <span class="i-lucide-paperclip" /> Anexar arquivo
                <input type="file" multiple hidden @change="onFileSelect" />
              </label>
            </div>
            <button
              class="em-btn em-btn-primary"
              :disabled="composeSending"
              @click="sendMail"
            >
              <span v-if="composeSending" class="em-spinner-sm" />
              <span v-else class="i-lucide-send" />
              {{ composeSending ? 'Enviando...' : 'Enviar' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- SETTINGS MODAL -->
    <Teleport to="body">
      <div v-if="settingsOpen" class="em-modal-backdrop" @click.self="settingsOpen = false">
        <div class="em-settings">
          <div class="em-compose-header">
            <h3>Contas de email</h3>
            <button class="em-btn em-btn-ghost em-btn-sm" @click="settingsOpen = false">
              <span class="i-lucide-x" />
            </button>
          </div>

          <div class="em-settings-body">
            <div v-if="accounts.length === 0" class="em-center em-empty" style="padding:40px">
              <span class="i-lucide-mail-x em-empty-icon" />
              <span class="em-muted">Nenhuma conta vinculada</span>
            </div>

            <div v-for="acc in accounts" :key="acc.id" class="em-account-card">
              <div class="em-account-info">
                <div class="em-account-avatar">
                  {{ acc.address.charAt(0).toUpperCase() }}
                </div>
                <div>
                  <div class="em-account-addr">{{ acc.address }}</div>
                  <div class="em-muted">{{ acc.name || 'Sem nome' }} · {{ acc.domain?.name }}</div>
                </div>
              </div>
              <div class="em-account-actions">
                <button class="em-btn em-btn-ghost em-btn-sm" @click="editSignature(acc)">
                  <span class="i-lucide-pen-line" /> Assinatura
                </button>
              </div>
            </div>

            <!-- Signature Editor -->
            <div v-if="editingAccountId" class="em-signature-editor">
              <h4>Editar assinatura</h4>
              <textarea v-model="signatureText" class="em-signature-textarea" placeholder="Cole sua assinatura HTML aqui..." />
              <div class="em-signature-actions">
                <button class="em-btn em-btn-ghost em-btn-sm" @click="editingAccountId = null">Cancelar</button>
                <button class="em-btn em-btn-primary em-btn-sm" @click="saveSignature">Salvar</button>
              </div>
            </div>

            <!-- Create Account -->
            <button v-if="!createAccountOpen" class="em-btn em-btn-ghost em-btn-full" @click="createAccountOpen = true">
              <span class="i-lucide-plus" /> Criar nova conta
            </button>
            <div v-else class="em-create-account">
              <h4>Nova conta de email</h4>
              <div class="em-compose-field">
                <label>Nome</label>
                <input v-model="newAccountName" placeholder="Nome exibição" class="em-input" />
              </div>
              <div class="em-compose-field em-email-compose">
                <label>Email</label>
                <div class="em-email-row">
                  <input v-model="newAccountLocal" placeholder="usuario" class="em-input em-input-local" />
                  <span class="em-at">@</span>
                  <select v-model="newAccountDomain" class="em-select em-select-domain">
                    <option value="">Domínio</option>
                    <option v-for="d in domains" :key="d.id" :value="d.id">{{ d.name }}</option>
                  </select>
                </div>
              </div>
              <div class="em-signature-actions">
                <button class="em-btn em-btn-ghost em-btn-sm" @click="createAccountOpen = false">Cancelar</button>
                <button class="em-btn em-btn-primary em-btn-sm" @click="createAccount">Criar</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.em-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-12));
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
}

/* ── Center / Loading ── */
.em-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
}

.em-spinner {
  width: 28px; height: 28px;
  border: 3px solid rgb(var(--slate-4));
  border-top-color: rgb(var(--blue-9));
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.em-spinner-sm {
  width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}

@keyframes spin { to { transform: rotate(360deg); } }

.em-muted { color: rgb(var(--slate-10)); font-size: 14px; }

.em-error-icon { font-size: 40px; color: rgb(var(--slate-8)); }

.em-empty { min-height: 300px; }
.em-empty-icon { font-size: 48px; color: rgb(var(--slate-6)); }

/* ── Toolbar ── */
.em-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 56px;
  flex-shrink: 0;
  border-bottom: 1px solid rgb(var(--slate-4));
  gap: 16px;
}

.em-toolbar-left { display: flex; align-items: center; gap: 10px; }
.em-toolbar-icon { font-size: 20px; color: rgb(var(--slate-11)); }
.em-toolbar-title { margin: 0; font-size: 16px; font-weight: 700; }

.em-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px;
  background: rgb(var(--blue-9)); color: #fff; font-size: 11px; font-weight: 700;
}

.em-toolbar-right { display: flex; align-items: center; gap: 10px; }

.em-search-box {
  display: flex; align-items: center; gap: 8px;
  background: rgb(var(--slate-2)); border: 1px solid rgb(var(--slate-4));
  border-radius: 8px; padding: 0 12px; height: 36px; min-width: 220px;
}

.em-search-icon { font-size: 16px; color: rgb(var(--slate-9)); flex-shrink: 0; }
.em-search-input { border: none; background: transparent; outline: none; font-size: 13px; color: rgb(var(--slate-12)); width: 100%; }
.em-search-input::placeholder { color: rgb(var(--slate-9)); }

/* ── Buttons ── */
.em-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  border: none; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; padding: 0 14px; height: 36px; transition: all 0.15s; white-space: nowrap;
}
.em-btn:hover { opacity: 0.9; }
.em-btn-primary { background: rgb(var(--blue-9)); color: #fff; }
.em-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.em-btn-ghost { background: transparent; color: rgb(var(--slate-11)); }
.em-btn-ghost:hover { background: rgb(var(--slate-3)); }
.em-btn-danger:hover { background: rgba(239, 68, 68, 0.1); color: #dc2626; }
.em-btn-sm { height: 32px; padding: 0 10px; font-size: 12px; }
.em-btn-full { width: 100%; margin-top: 12px; }
.em-btn-compose { padding: 0 18px; }

/* ── Content ── */
.em-content { flex: 1; overflow-y: auto; }

/* ── Email List ── */
.em-list-container { display: flex; flex-direction: column; height: 100%; }
.em-list { flex: 1; }

.em-row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 24px; cursor: pointer;
  border-bottom: 1px solid rgb(var(--slate-3)); transition: background 0.1s;
}
.em-row:hover { background: rgb(var(--slate-2)); }
.em-row--unread { background: rgba(var(--blue-3), 0.3); }
.em-row--unread .em-row-sender, .em-row--unread .em-row-subject { font-weight: 700; }

.em-star-btn {
  flex-shrink: 0; width: 28px; height: 28px; border: none; background: transparent;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  border-radius: 4px; color: rgb(var(--slate-7)); font-size: 16px;
}
.em-star-btn:hover { color: #f59e0b; }
.em-star-btn--active { color: #f59e0b; }

.em-row-sender {
  width: 200px; flex-shrink: 0; font-size: 14px; color: rgb(var(--slate-12));
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  display: flex; align-items: center; gap: 6px;
}

.em-thread-badge {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 1px 6px; border-radius: 10px;
  background: rgba(var(--blue-9), 0.12); color: rgb(var(--blue-9));
  font-size: 11px; font-weight: 700; flex-shrink: 0;
}

.em-thread-icon { font-size: 11px; }

.em-row-content {
  flex: 1; min-width: 0; white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; font-size: 14px;
}
.em-row-subject { color: rgb(var(--slate-12)); }
.em-row-preview { color: rgb(var(--slate-9)); }

.em-row-meta { flex-shrink: 0; display: flex; align-items: center; gap: 8px; }
.em-row-attach { font-size: 14px; color: rgb(var(--slate-9)); }
.em-row-date { font-size: 12px; color: rgb(var(--slate-10)); white-space: nowrap; font-variant-numeric: tabular-nums; }

/* ── Email Detail ── */
.em-detail { display: flex; flex-direction: column; height: 100%; }

.em-detail-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 24px; border-bottom: 1px solid rgb(var(--slate-3)); flex-shrink: 0;
}
.em-detail-actions { display: flex; gap: 4px; }
.em-star-active { color: #f59e0b !important; }

.em-detail-header { padding: 24px 28px 0; }
.em-detail-subject { margin: 0 0 8px; font-size: 22px; font-weight: 700; line-height: 1.3; }

.em-thread-info {
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; color: rgb(var(--slate-10)); margin-bottom: 16px;
  padding-bottom: 16px; border-bottom: 1px solid rgb(var(--slate-3));
}

.em-detail-meta {
  display: flex; align-items: center; gap: 14px;
  padding-bottom: 16px;
}

.em-detail-avatar {
  width: 36px; height: 36px; border-radius: 50%;
  background: rgb(var(--blue-9)); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700; flex-shrink: 0;
}

.em-detail-info { flex: 1; min-width: 0; }
.em-detail-from { font-size: 14px; font-weight: 600; color: rgb(var(--slate-12)); }
.em-detail-email { font-weight: 400; font-size: 12px; color: rgb(var(--slate-10)); margin-left: 4px; }
.em-detail-to { font-size: 13px; color: rgb(var(--slate-10)); }
.em-detail-date { font-size: 12px; color: rgb(var(--slate-10)); white-space: nowrap; flex-shrink: 0; }

.em-detail-body { flex: 1; overflow-y: auto; padding: 0 28px 24px; }

.em-thread-message { padding-top: 16px; }
.em-thread-message--border { border-top: 1px solid rgb(var(--slate-4)); margin-top: 16px; }

.em-msg-body { padding-left: 50px; }

.em-html-body { font-size: 14px; line-height: 1.6; word-break: break-word; }
.em-html-body :deep(img) { max-width: 100%; height: auto; }
.em-text-body {
  margin: 0; font-family: system-ui, sans-serif; font-size: 14px;
  line-height: 1.6; white-space: pre-wrap; word-break: break-word; color: rgb(var(--slate-12));
}

.em-detail-attachments { padding: 12px 0 0 50px; }
.em-attach-label { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: rgb(var(--slate-11)); margin-bottom: 8px; }
.em-attach-list { display: flex; flex-wrap: wrap; gap: 8px; }
.em-attach-item {
  display: flex; align-items: center; gap: 6px; padding: 8px 12px;
  border-radius: 8px; border: 1px solid rgb(var(--slate-4)); font-size: 13px; color: rgb(var(--slate-12));
}

/* ── Compose Modal ── */
.em-modal-backdrop {
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.4);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
}
.em-compose {
  width: 640px; max-width: 95vw; max-height: 85vh;
  background: rgb(var(--solid-1)); border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  display: flex; flex-direction: column; overflow: hidden;
}
.em-compose-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid rgb(var(--slate-4));
}
.em-compose-header h3 { margin: 0; font-size: 15px; font-weight: 700; }

.em-alert {
  margin: 12px 20px 0; padding: 10px 14px; border-radius: 8px;
  background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.2);
  font-size: 13px; color: #dc2626;
}

.em-compose-scroll { flex: 1; overflow-y: auto; min-height: 0; }
.em-compose-fields { padding: 16px 20px 0; display: flex; flex-direction: column; gap: 10px; }
.em-compose-field { display: flex; flex-direction: column; gap: 4px; }
.em-compose-field label { font-size: 12px; font-weight: 600; color: rgb(var(--slate-10)); text-transform: uppercase; letter-spacing: 0.04em; }

.em-field-row { display: flex; align-items: center; justify-content: space-between; }
.em-ccbcc-toggle { border: none; background: transparent; color: rgb(var(--blue-9)); font-size: 12px; font-weight: 600; cursor: pointer; padding: 0; }
.em-ccbcc-toggle:hover { text-decoration: underline; }

.em-input, .em-select {
  height: 38px; padding: 0 12px; border: 1px solid rgb(var(--slate-5));
  border-radius: 8px; font-size: 14px; color: rgb(var(--slate-12));
  background: rgb(var(--solid-1)); outline: none;
}
.em-input:focus, .em-select:focus { border-color: rgb(var(--blue-9)); }

.em-compose-body {
  flex: 1; min-height: 200px; margin: 12px 20px; padding: 12px;
  border: 1px solid rgb(var(--slate-5)); border-radius: 8px;
  font-size: 14px; line-height: 1.6; color: rgb(var(--slate-12));
  background: rgb(var(--solid-1)); outline: none; resize: vertical;
  font-family: system-ui, sans-serif;
}
.em-compose-body:focus { border-color: rgb(var(--blue-9)); }

.em-compose-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 20px; border-top: 1px solid rgb(var(--slate-4)); gap: 12px;
}
.em-compose-footer-left { display: flex; align-items: center; gap: 8px; flex: 1; }
.em-attach-btn { cursor: pointer; }

.em-attach-preview-area {
  margin: 0 20px; padding: 12px; border: 1px solid rgb(var(--slate-4));
  border-radius: 8px; background: rgb(var(--slate-2));
}
.em-attach-preview-label { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: rgb(var(--slate-10)); margin-bottom: 10px; }
.em-attach-grid { display: flex; flex-wrap: wrap; gap: 8px; }
.em-attach-card {
  position: relative; width: 120px; border: 1px solid rgb(var(--slate-4));
  border-radius: 8px; overflow: hidden; background: rgb(var(--solid-1)); transition: border-color 0.15s;
}
.em-attach-card:hover { border-color: rgb(var(--slate-7)); }
.em-attach-card-remove {
  position: absolute; top: 4px; right: 4px; width: 20px; height: 20px;
  border-radius: 50%; border: none; background: rgba(0, 0, 0, 0.5); color: #fff;
  font-size: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center;
  opacity: 0; transition: opacity 0.12s; z-index: 2;
}
.em-attach-card:hover .em-attach-card-remove { opacity: 1; }
.em-attach-card-remove:hover { background: #dc2626; }
.em-attach-card-img { width: 120px; height: 80px; overflow: hidden; display: flex; align-items: center; justify-content: center; background: rgb(var(--slate-3)); }
.em-attach-card-img img { width: 100%; height: 100%; object-fit: cover; }
.em-attach-card-icon { width: 120px; height: 80px; display: flex; align-items: center; justify-content: center; background: rgb(var(--slate-2)); font-size: 28px; color: rgb(var(--slate-8)); }
.em-attach-card-info { padding: 6px 8px; display: flex; flex-direction: column; gap: 2px; }
.em-attach-card-name { font-size: 11px; font-weight: 500; color: rgb(var(--slate-12)); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.em-attach-card-size { font-size: 10px; color: rgb(var(--slate-9)); font-variant-numeric: tabular-nums; }

/* ── Settings Modal ── */
.em-settings {
  width: 520px; max-width: 95vw; max-height: 80vh;
  background: rgb(var(--solid-1)); border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  display: flex; flex-direction: column; overflow: hidden;
}
.em-settings-body { flex: 1; overflow-y: auto; padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; }
.em-account-card { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-radius: 10px; border: 1px solid rgb(var(--slate-4)); }
.em-account-info { display: flex; align-items: center; gap: 12px; }
.em-account-avatar { width: 36px; height: 36px; border-radius: 50%; background: rgb(var(--blue-9)); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; flex-shrink: 0; }
.em-account-addr { font-size: 14px; font-weight: 600; color: rgb(var(--slate-12)); }
.em-signature-editor { padding: 16px; border-radius: 10px; border: 1px solid rgb(var(--slate-4)); background: rgb(var(--slate-2)); }
.em-signature-editor h4 { margin: 0 0 10px; font-size: 14px; font-weight: 700; }
.em-signature-textarea { width: 100%; min-height: 120px; padding: 10px; border: 1px solid rgb(var(--slate-5)); border-radius: 8px; font-size: 13px; font-family: monospace; color: rgb(var(--slate-12)); background: rgb(var(--solid-1)); outline: none; resize: vertical; }
.em-signature-textarea:focus { border-color: rgb(var(--blue-9)); }
.em-signature-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 10px; }
.em-create-account { padding: 16px; border-radius: 10px; border: 1px solid rgb(var(--slate-4)); background: rgb(var(--slate-2)); }
.em-create-account h4 { margin: 0 0 12px; font-size: 14px; font-weight: 700; }
.em-email-row { display: flex; align-items: center; gap: 4px; }
.em-input-local { flex: 1; }
.em-at { font-size: 14px; color: rgb(var(--slate-10)); font-weight: 600; }
.em-select-domain { flex: 1; }
</style>
